import type { Delta } from 'quill/core';
import React from 'react';
import * as styles from './CommentResultCard.module.scss';
import AvatarWithAction from '../components/AvatarWithAction';
import Badge from '../components/Badge';
import Card, { CardSeparator } from '../components/Card';
import CardHeader from '../components/CardHeader';
import Timestamp from '../components/Timestamp';
import WrappingTwoColumnLayout from '../components/WrappingTwoColumnLayout';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import CommentPreview from '../post/comment/CommentPreview';
import PostPill from '../post/PostPill';
import { useGetUser } from '../user/useAllUsers';
import UserDetailPreviewPopOver from '../user/UserDetailPreviewPopOver';
import commentThreadPath from '../utils/url/commentThreadPath';

const COMMENT_FRAGMENT = gql(`
  # eslint-disable-next-line @graphql-eslint/require-selections
  fragment CommentResultCard_comment on Comment {
    insertedAt
    author {
      id
      ...useGetUser_user
    }
    thread {
      ...commentThreadPath_thread
    }
  }
`);

interface CommentResultCardProps {
  content: Delta;
  comment: DocumentType<typeof COMMENT_FRAGMENT>;
}

const CommentResultCard = ({ content, comment }: CommentResultCardProps) => {
  const getUser = useGetUser();
  const author = getUser(comment.author);

  return (
    <Card to={commentThreadPath(comment.thread)}>
      <CardHeader
        icon={
          <div className={styles.avatarContainer}>
            <UserDetailPreviewPopOver userId={author.id} placement="top-start">
              <AvatarWithAction user={author} action="COMMENT_CREATE" />
            </UserDetailPreviewPopOver>
          </div>
        }
        meta={
          <>
            <Timestamp date={comment.insertedAt} /> by {author.name}
          </>
        }
        title={
          <div className={styles.comment}>
            <CommentPreview content={content} />
          </div>
        }
      />
      <CardSeparator />
      <WrappingTwoColumnLayout
        left={
          <>
            <span className={styles.context}>Comment in</span>
            <PostPill post={comment.thread.post} />
          </>
        }
        right={
          !!comment.thread.resolvedAt && <Badge color="gray">Resolved</Badge>
        }
      />
    </Card>
  );
};

export default CommentResultCard;
